import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../api/auth';


const PRESETS = [
    { q: '영업시간이 어떻게 되나요?',       a: '' },
    { q: '주차가 가능한가요?',               a: '' },
    { q: '가격이 어떻게 되나요?',            a: '' },
    { q: '예약은 어떻게 하나요?',            a: '' },
    { q: '위치가 어디인가요?',               a: '' },
    { q: '당일 예약이 가능한가요?',          a: '' },
    { q: '취소나 변경은 어떻게 하나요?',     a: '' },
    { q: '어떤 서비스를 제공하시나요?',      a: '' },
];

let nextId = 1;
const makeItem = (q = '', a = '') => ({ id: nextId++, q, a });


const FaqOnboardingPage = () => {
    const navigate = useNavigate();
    const { setIsLoggedIn } = useAuth();

    const [items, setItems] = useState([makeItem()]);
    const [editingId, setEditingId] = useState(1);

    const handleLogout = async () => {
        try {
            await logout();
            setIsLoggedIn(false);
            navigate('/auth/login', { replace: true });
        } catch {
            alert('로그아웃 중 오류가 발생했습니다.');
        }
    };

    const addPreset = (preset) => {
        const alreadyExists = items.some((it) => it.q === preset.q);
        if (alreadyExists) return;
        const item = makeItem(preset.q, preset.a);
        setItems((prev) => [...prev, item]);
        setEditingId(item.id);
    };

    const addBlank = () => {
        const item = makeItem();
        setItems((prev) => [...prev, item]);
        setEditingId(item.id);
    };

    const updateItem = (id, field, value) => {
        setItems((prev) => prev.map((it) => it.id === id ? { ...it, [field]: value } : it));
    };

    const deleteItem = (id) => {
        setItems((prev) => {
            const next = prev.filter((it) => it.id !== id);
            return next.length === 0 ? [makeItem()] : next;
        });
        setEditingId(null);
    };

    const filledCount = items.filter((it) => it.q.trim() && it.a.trim()).length;

    return (
        <Page>
            {/* Header */}
            <TopBar>
                <TopLogo>ddiring</TopLogo>
                <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
            </TopBar>

            <Body>
                <Inner>
                    {/* Title */}
                    <TitleArea>
                        <PageBadge>자주 묻는 질문</PageBadge>
                        <PageTitle>고객이 자주 묻는 질문을 추가해요</PageTitle>
                        <PageDesc>
                            AI가 이 내용을 바탕으로 고객에게 직접 답변합니다.<br />
                            나중에 언제든 수정할 수 있어요.
                        </PageDesc>
                    </TitleArea>

                    {/* Preset chips */}
                    <ChipSection>
                        <ChipLabel>빠르게 추가하기</ChipLabel>
                        <ChipList>
                            {PRESETS.map((p) => {
                                const added = items.some((it) => it.q === p.q);
                                return (
                                    <Chip
                                        key={p.q}
                                        $added={added}
                                        onClick={() => !added && addPreset(p)}
                                        disabled={added}
                                    >
                                        {added ? '✓ ' : '+ '}{p.q}
                                    </Chip>
                                );
                            })}
                        </ChipList>
                    </ChipSection>

                    {/* FAQ List */}
                    <FaqList>
                        {items.map((item, idx) => {
                            const isEditing = editingId === item.id;
                            return (
                                <FaqCard
                                    key={item.id}
                                    $editing={isEditing}
                                    onClick={() => !isEditing && setEditingId(item.id)}
                                >
                                    <FaqCardTop>
                                        <FaqIndex>Q{idx + 1}</FaqIndex>
                                        <DeleteBtn
                                            onClick={(e) => { e.stopPropagation(); deleteItem(item.id); }}
                                            title="삭제"
                                        >
                                            ✕
                                        </DeleteBtn>
                                    </FaqCardTop>

                                    {isEditing ? (
                                        <FaqInputs>
                                            <QuestionInput
                                                placeholder="질문을 입력하세요  예: 영업시간이 어떻게 되나요?"
                                                value={item.q}
                                                onChange={(e) => updateItem(item.id, 'q', e.target.value)}
                                                autoFocus
                                            />
                                            <AnswerTextarea
                                                placeholder="답변을 입력하세요  예: 평일 오전 10시부터 오후 8시까지 운영합니다."
                                                value={item.a}
                                                onChange={(e) => updateItem(item.id, 'a', e.target.value)}
                                                rows={3}
                                            />
                                            <SaveBtn
                                                onClick={(e) => { e.stopPropagation(); setEditingId(null); }}
                                                disabled={!item.q.trim() || !item.a.trim()}
                                            >
                                                저장
                                            </SaveBtn>
                                        </FaqInputs>
                                    ) : (
                                        <FaqPreview>
                                            <FaqQ $empty={!item.q}>{item.q || '질문을 입력해주세요'}</FaqQ>
                                            <FaqA $empty={!item.a}>{item.a || '답변을 입력해주세요'}</FaqA>
                                        </FaqPreview>
                                    )}
                                </FaqCard>
                            );
                        })}

                        <AddButton onClick={addBlank}>
                            + 직접 추가하기
                        </AddButton>
                    </FaqList>

                    {/* Bottom actions */}
                    <BottomArea>
                        <CountText>
                            {filledCount > 0
                                ? <><Strong>{filledCount}개</Strong> 질문이 등록됐어요</>
                                : '질문을 1개 이상 등록하면 AI가 더 잘 응대할 수 있어요'
                            }
                        </CountText>
                        <ButtonRow>
                            <SkipBtn onClick={() => navigate('/stores')}>
                                나중에 할게요
                            </SkipBtn>
                            <DoneBtn
                                onClick={() => navigate('/stores')}
                                disabled={filledCount === 0}
                            >
                                완료하고 시작하기
                            </DoneBtn>
                        </ButtonRow>
                    </BottomArea>
                </Inner>
            </Body>
        </Page>
    );
};


/* ─────────────────────────────────────────────
   Styled Components
───────────────────────────────────────────── */
const Page = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
    font-family: Pretendard, -apple-system, sans-serif;
`;

const TopBar = styled.div`
    position: fixed;
    top: 0; left: 0;
    width: 100%;
    height: 64px;
    padding: 0 32px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #ffffff;
    border-bottom: 1px solid #eeeeee;
    z-index: 100;
`;

const TopLogo = styled.div`
    font-size: 18px;
    font-weight: 800;
    color: #487BFD;
    letter-spacing: -0.02em;
`;

const LogoutButton = styled.button`
    background: none;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    padding: 6px 14px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
    &:hover { border-color: #bbbbbb; color: #555555; }
`;

const Body = styled.div`
    padding-top: 88px;
    padding-bottom: 60px;
    display: flex;
    justify-content: center;
`;

const Inner = styled.div`
    width: 100%;
    max-width: 640px;
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

/* Title */
const TitleArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 8px;
`;

const PageBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.1em;
    text-transform: uppercase;
`;

const PageTitle = styled.h1`
    font-size: 24px;
    font-weight: 800;
    color: #111111;
    letter-spacing: -0.02em;
    margin: 0;
`;

const PageDesc = styled.p`
    font-size: 14px;
    color: #999999;
    line-height: 1.65;
    margin: 0;
`;

/* Preset chips */
const ChipSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ChipLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #aaaaaa;
`;

const ChipList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Chip = styled.button`
    background: ${({ $added }) => $added ? '#eef2ff' : '#ffffff'};
    border: 1px solid ${({ $added }) => $added ? '#c0d0ff' : '#e0e0e0'};
    border-radius: 100px;
    padding: 7px 14px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: ${({ $added }) => $added ? '#487BFD' : '#555555'};
    cursor: ${({ $added }) => $added ? 'default' : 'pointer'};
    transition: border-color 0.15s, background 0.15s, color 0.15s;

    &:hover:not(:disabled) {
        border-color: #487BFD;
        color: #487BFD;
        background: #f5f8ff;
    }
`;

/* FAQ List */
const FaqList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const FaqCard = styled.div`
    background: #ffffff;
    border: 1.5px solid ${({ $editing }) => $editing ? '#487BFD' : '#eeeeee'};
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    cursor: ${({ $editing }) => $editing ? 'default' : 'pointer'};
    transition: border-color 0.15s, box-shadow 0.15s;
    box-shadow: ${({ $editing }) => $editing ? '0 0 0 4px rgba(72,123,253,0.08)' : 'none'};

    &:hover {
        border-color: ${({ $editing }) => $editing ? '#487BFD' : '#cccccc'};
    }
`;

const FaqCardTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const FaqIndex = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.06em;
`;

const DeleteBtn = styled.button`
    background: none;
    border: none;
    font-size: 13px;
    color: #cccccc;
    cursor: pointer;
    padding: 2px 4px;
    line-height: 1;
    transition: color 0.15s;
    &:hover { color: #EF4444; }
`;

/* Editing state */
const FaqInputs = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const inputBase = `
    width: 100%;
    box-sizing: border-box;
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    padding: 10px 14px;
    font-family: Pretendard;
    font-size: 14px;
    color: #111111;
    outline: none;
    resize: none;
    transition: border-color 0.15s;

    &::placeholder { color: #c0c0c0; }
    &:focus { border-color: #487BFD; background: #ffffff; }
`;

const QuestionInput = styled.input`${inputBase}`;
const AnswerTextarea = styled.textarea`${inputBase}`;

const SaveBtn = styled.button`
    align-self: flex-end;
    background: #487BFD;
    border: none;
    border-radius: 8px;
    padding: 8px 20px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* Preview state */
const FaqPreview = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FaqQ = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: ${({ $empty }) => $empty ? '#cccccc' : '#111111'};
    margin: 0;
    font-style: ${({ $empty }) => $empty ? 'italic' : 'normal'};
`;

const FaqA = styled.p`
    font-size: 13px;
    color: ${({ $empty }) => $empty ? '#cccccc' : '#777777'};
    margin: 0;
    line-height: 1.6;
    font-style: ${({ $empty }) => $empty ? 'italic' : 'normal'};
`;

const AddButton = styled.button`
    background: #ffffff;
    border: 1.5px dashed #d8d8d8;
    border-radius: 16px;
    padding: 16px;
    width: 100%;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;

    &:hover {
        border-color: #487BFD;
        color: #487BFD;
        background: #f5f8ff;
    }
`;

/* Bottom */
const BottomArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 8px;
`;

const CountText = styled.p`
    font-size: 13px;
    color: #aaaaaa;
    margin: 0;
    text-align: center;
`;

const Strong = styled.strong`
    color: #487BFD;
    font-weight: 700;
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 10px;
`;

const SkipBtn = styled.button`
    flex: 1;
    background: none;
    border: 1px solid #e5e5e5;
    border-radius: 12px;
    padding: 13px 0;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
    &:hover { border-color: #bbbbbb; color: #555555; }
`;

const DoneBtn = styled.button`
    flex: 2;
    background: #487BFD;
    border: none;
    border-radius: 12px;
    padding: 13px 0;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.88; }
    &:disabled { opacity: 0.35; cursor: not-allowed; }
`;


export default FaqOnboardingPage;
